import api from './api';

export const appointmentService = {
  list: async (params = {}) => {
    const response = await api.get('/appointments', { params });
    return response.data;
  },

  get: async (id) => {
    const response = await api.get(`/appointments/${id}`);
    return response.data;
  },

  history: async (id) => {
    const response = await api.get(`/appointments/${id}/history`);
    return response.data;
  },

  /** scheduledDoctorId: the slots of that doctor's own schedule (one patient per slot) instead of the centre's. */
  slots: async (date, centreId, departmentId, scheduledDoctorId) => {
    const response = await api.get('/appointments/slots', {
      params: { date, centreId: centreId || undefined, departmentId: departmentId || undefined, scheduledDoctorId: scheduledDoctorId || undefined },
    });
    return response.data;
  },

  /** Doctors that can be booked at the centre: only those with a schedule. */
  doctors: async (centreId) => {
    const response = await api.get('/appointments/doctors', { params: { centreId: centreId || undefined } });
    return response.data;
  },

  doctorSchedule: async (doctorId, centreId) => {
    const response = await api.get('/appointments/doctor-schedule', { params: { doctorId, centreId: centreId || undefined } });
    return response.data;
  },

  /** data.saved is false when nothing was written: show data.conflicts, then send again with acknowledgeConflicts. */
  saveDoctorSchedule: async (payload) => {
    const response = await api.put('/appointments/doctor-schedule', payload);
    return response.data;
  },

  doctorLeaves: async (centreId, doctorId) => {
    const response = await api.get('/appointments/doctor-leave', { params: { centreId: centreId || undefined, doctorId: doctorId || undefined } });
    return response.data;
  },

  addDoctorLeave: async (payload) => {
    const response = await api.post('/appointments/doctor-leave', payload);
    return response.data;
  },

  deleteDoctorLeave: async (leaveId) => {
    const response = await api.delete(`/appointments/doctor-leave/${leaveId}`);
    return response.data;
  },

  /** Open appointments whose doctor has no matching slot any more. Staff reschedule them. */
  doctorConflicts: async (centreId) => {
    const response = await api.get('/appointments/doctor-conflicts', { params: { centreId: centreId || undefined } });
    return response.data;
  },

  /** Today's open appointments of this centre for a UHID or mobile (used by the registration screen). */
  forPatient: async ({ patientId, mobile } = {}) => {
    const response = await api.get('/appointments/for-patient', { params: { patientId: patientId || undefined, mobile: mobile || undefined } });
    return response.data;
  },

  /** The patient has arrived: confirms a booked appointment and gives the queue token. */
  arrive: async (id, data = {}) => {
    const response = await api.post(`/appointments/${id}/arrive`, data);
    return response.data;
  },

  reminder: async (id, channel) => {
    const response = await api.post(`/appointments/${id}/reminder`, { channel });
    return response.data;
  },

  createRecurring: async (data) => {
    const config = data && data.idempotencyKey ? { headers: { 'X-Idempotency-Key': data.idempotencyKey } } : {};
    const response = await api.post('/appointments/recurring', data, config);
    return response.data;
  },

  report: async (from, to, centreId) => {
    const response = await api.get('/appointments/report', { params: { from, to, centreId: centreId || undefined } });
    return response.data;
  },

  exportRows: async (params = {}) => {
    const response = await api.get('/appointments/export', { params });
    return response.data;
  },

  listHolidays: async (centreId, from, to) => {
    const response = await api.get('/appointments/holidays', { params: { centreId: centreId || undefined, from: from || undefined, to: to || undefined } });
    return response.data;
  },

  addHoliday: async (data) => {
    const response = await api.post('/appointments/holidays', data);
    return response.data;
  },

  deleteHoliday: async (id) => {
    const response = await api.delete(`/appointments/holidays/${id}`);
    return response.data;
  },

  listDepartmentCapacity: async (centreId) => {
    const response = await api.get('/appointments/department-capacity', { params: { centreId: centreId || undefined } });
    return response.data;
  },

  saveDepartmentCapacity: async (data) => {
    const response = await api.put('/appointments/department-capacity', data);
    return response.data;
  },

  deleteDepartmentCapacity: async (centreId, departmentId) => {
    const response = await api.delete('/appointments/department-capacity', { params: { centreId: centreId || undefined, departmentId } });
    return response.data;
  },

  departments: async () => {
    const response = await api.get('/appointments/departments');
    return response.data;
  },

  create: async (data) => {
    const config = data && data.idempotencyKey ? { headers: { 'X-Idempotency-Key': data.idempotencyKey } } : {};
    const response = await api.post('/appointments', data, config);
    return response.data;
  },

  reschedule: async (id, data) => {
    const response = await api.put(`/appointments/${id}/reschedule`, data);
    return response.data;
  },

  confirm: async (id, data = {}) => {
    const response = await api.post(`/appointments/${id}/confirm`, data);
    return response.data;
  },

  cancel: async (id, data) => {
    const response = await api.post(`/appointments/${id}/cancel`, data);
    return response.data;
  },

  noShow: async (id, data = {}) => {
    const response = await api.post(`/appointments/${id}/no-show`, data);
    return response.data;
  },

  getConfig: async (centreId) => {
    const response = await api.get('/appointments/config', { params: { centreId: centreId || undefined } });
    return response.data;
  },

  saveConfig: async (data) => {
    const response = await api.put('/appointments/config', data);
    return response.data;
  },
};

export default appointmentService;
